import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useParentScope } from "@/context/ParentContext";
import { EmptyState, ErrorState, Loading } from "@/components/States";
import { ParentCalendarContent } from "@/pages/parent/CalendarContent";

const EXAM_PAGE_SIZE = 20;

export default function ParentCalendar() {
  const { student } = useParentScope();
  const [subjects, setSubjects] = useState(null);
  const [exams, setExams] = useState([]);
  const [error, setError] = useState("");
  const [subjectForm, setSubjectForm] = useState({ name: "", color: "blue" });
  const [examForm, setExamForm] = useState({ title: "", subject_id: "", exam_date: "" });
  const [scoreDraft, setScoreDraft] = useState({});
  const [examOffset, setExamOffset] = useState(0);
  const [examTotal, setExamTotal] = useState(0);

  const load = useCallback(async () => {
    if (!student) return;
    setError("");
    try {
      const [s, e] = await Promise.all([
        api.get("/parent/subjects", { params: { student_id: student.id, limit: 100 } }),
        api.get("/parent/exams", {
          params: { student_id: student.id, limit: EXAM_PAGE_SIZE, offset: examOffset },
        }),
      ]);
      setSubjects(s.data);
      setExams(e.data);
      setExamTotal(Number(e.headers["x-total-count"] || e.data.length));
    } catch (err) {
      setError(apiError(err, "No pudimos cargar materias y examenes."));
      setSubjects([]);
    }
  }, [student, examOffset]);

  useEffect(() => {
    setExamOffset(0);
  }, [student?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const addSubject = async (event) => {
    event.preventDefault();
    try {
      await api.post("/parent/subjects", { student_id: student.id, ...subjectForm });
      setSubjectForm({ name: "", color: "blue" });
      toast.success("Materia creada");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const addExam = async (event) => {
    event.preventDefault();
    try {
      await api.post("/parent/exams", { student_id: student.id, ...examForm });
      setExamForm({ title: "", subject_id: "", exam_date: "" });
      toast.success("Examen registrado");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveScore = async (exam) => {
    const draft = scoreDraft[exam.id] || {};
    try {
      await api.patch(`/parent/exams/${exam.id}`, {
        status: "done",
        real_score: draft.score === undefined || draft.score === "" ? null : Number(draft.score),
        real_score_max: draft.max === undefined || draft.max === "" ? 10 : Number(draft.max),
      });
      toast.success("Nota real guardada");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const uploadCorrectedExam = async (exam) => {
    const draft = scoreDraft[exam.id] || {};
    if (!draft.file || draft.score === undefined || draft.score === "") {
      toast.error("Selecciona el examen corregido e indica la nota");
      return;
    }
    try {
      const form = new FormData();
      form.append("file", draft.file);
      const upload = await api.post(`/parent/exams/${exam.id}/real-submissions`, form);
      if (upload.data.possible_duplicate) {
        toast.warning("El archivo se parece a otro examen. Quedó pendiente de revisión y no generó XP.");
        await load();
        return;
      }
      await api.patch(`/parent/exams/${exam.id}/real-submissions/${upload.data.id}`, {
        status: "verified",
        score: Number(draft.score),
        score_max: draft.max === undefined || draft.max === "" ? 10 : Number(draft.max),
        error_topic_ids: [],
      });
      setScoreDraft((current) => ({ ...current, [exam.id]: {} }));
      toast.success("Examen verificado y XP calculado");
      await load();
    } catch (err) {
      toast.error(apiError(err, "No pudimos verificar el examen corregido."));
    }
  };

  const removeExam = async (exam) => {
    if (!window.confirm(`¿Eliminar el examen "${exam.title}"? El material de estudio se conservará.`)) return;
    try {
      await api.delete(`/parent/exams/${exam.id}`);
      setExams((current) => current.filter((item) => item.id !== exam.id));
      setExamTotal((current) => Math.max(0, current - 1));
      setScoreDraft((current) => {
        const next = { ...current };
        delete next[exam.id];
        return next;
      });
      toast.success("Examen eliminado");
      await load();
    } catch (err) {
      toast.error(apiError(err, "No pudimos eliminar el examen."));
    }
  };

  const removeSubject = async (id) => {
    try {
      await api.delete(`/parent/subjects/${id}`);
      toast.success("Materia eliminada");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!student) return <EmptyState title="Selecciona un hijo" description="Crea primero un perfil de estudiante." />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!subjects) return <Loading />;

  return (
    <ParentCalendarContent
      student={student}
      subjects={subjects}
      subjectForm={subjectForm}
      onSubjectFormChange={setSubjectForm}
      onAddSubject={addSubject}
      onRemoveSubject={removeSubject}
      exams={exams}
      examForm={examForm}
      onExamFormChange={setExamForm}
      onAddExam={addExam}
      scoreDraft={scoreDraft}
      onScoreDraftChange={setScoreDraft}
      onSaveScore={saveScore}
      onUploadCorrectedExam={uploadCorrectedExam}
      onRemoveExam={removeExam}
      examOffset={examOffset}
      examTotal={examTotal}
      examPageSize={EXAM_PAGE_SIZE}
      onExamPageChange={setExamOffset}
    />
  );
}
